import {
  Applicant, LocationOption, ProfessionGroups, WorkersFilterSettingsResponse, getWorkersFiltersSettings, getWorkersGroups, getWorkersList,
} from '@/api/Workers';
import store from '@/store';
import { ProfessionQuery } from '@/types/ProfessionType';
import {
  VuexModule, Module, getModule, Action, Mutation,
} from 'vuex-module-decorators';

export type SelectedCluster = {
  id: number
  title: string
}

export type FiltersState = {
  isImmediately: boolean
  orderDays: number | null
  orderMonth: number | null
  orderYear: number | null
  selectedLand: LocationOption['location_id'] | null
  salarySize: number | null
  selectedSalaryTypeId: number
  selectedLevelOfStudy: string[]
  selectedTypeOfActivity: string[]
  selectedRequirementLevel: string[]
  selectedLanguagesLevel: {
    language: number | null
    level: number | null
  }[]
}

type UpdateFilterPayload<T extends keyof FiltersState> = {
  propertyName: T
  value: FiltersState[T]
};

export interface IWorkersState {
  selectedWorkersTab: number
  searchWorkerProfession: ProfessionQuery | null
  professionGroups: ProfessionGroups | null
  workersList: Applicant[]
  showWorkers: { [key in 'firstTab' | 'secondTab']: boolean }
  workersFilters: WorkersFilterSettingsResponse | null
  selectedWorkerId: Applicant['id'] | null
  selectedCluster: SelectedCluster | null
  selectedFilterType: 'profession' | 'cluster'
  filtersState: FiltersState
}

const DEFAULT_SHOW_WORKERS = {
  firstTab: true,
  secondTab: false,
};

const DEFAULT_PAGINATION = {
  page: 1,
  perPage: 20,
  total: 0,
};

const DEFAULT_FILTERS_STATE: FiltersState = {
  isImmediately: false,
  orderDays: null,
  orderMonth: null,
  orderYear: null,
  selectedLand: null,
  salarySize: null,
  selectedSalaryTypeId: 1,
  selectedLevelOfStudy: [],
  selectedTypeOfActivity: [],
  selectedRequirementLevel: [],
  selectedLanguagesLevel: [
    {
      language: null,
      level: null,
    },
    {
      language: null,
      level: null,
    },
  ],
};

@Module({ dynamic: true, store, name: 'WorkersModule' })
class Workers extends VuexModule implements IWorkersState {
  selectedWorkersTab: IWorkersState['selectedWorkersTab'] = 0;

  searchWorkerProfession: IWorkersState['searchWorkerProfession'] = null;

  professionGroups: IWorkersState['professionGroups'] = null;

  workersList: Applicant[] = [];

  showWorkers: IWorkersState['showWorkers'] = { ...DEFAULT_SHOW_WORKERS };

  workersListPagination = { ...DEFAULT_PAGINATION };

  workersFilters: IWorkersState['workersFilters'] = null;

  selectedWorkerId: IWorkersState['selectedWorkerId'] = null;

  selectedCluster: IWorkersState['selectedCluster'] = null;

  selectedFilterType: IWorkersState['selectedFilterType'] = 'profession';

  filtersState: IWorkersState['filtersState'] = JSON.parse(JSON.stringify(DEFAULT_FILTERS_STATE));

  get filterByLanguageQuality() {
    return this.filtersState.selectedLanguagesLevel
      .filter((item) => item.language !== null && item.level !== null)
      .map((item) => `${item.language}_${item.level}`)
      .join(',');
  }

  get filterByLevelOfStudy() {
    return this.filtersState.selectedLevelOfStudy.reduce((obj: { [key: string]: string }, key) => ({ ...obj, [key]: 'on' }), {});
  }

  get filterByTypeOfActivity() {
    return this.filtersState.selectedTypeOfActivity.reduce((obj: { [key: string]: string }, key) => ({ ...obj, [key]: 'on' }), {});
  }

  get filterByRequirementLevel() {
    return this.filtersState.selectedRequirementLevel.reduce((obj: { [key: string]: string }, key) => ({ ...obj, [key]: 'on' }), {});
  }

  @Mutation
  SET_SELECTED_TAB(payload: IWorkersState['selectedWorkersTab']) {
    this.selectedWorkersTab = payload;
  }

  // Filters

  @Mutation
  SET_FILTERS_STATE<T extends keyof FiltersState>({ propertyName, value }: UpdateFilterPayload<T>) {
    this.filtersState[propertyName] = value;
  }

  // End filters

  @Mutation
  SET_SELECTED_FILTER_TYPE(payload: IWorkersState['selectedFilterType']) {
    this.selectedFilterType = payload;
  }

  @Mutation
  SET_SELECTED_WORKER(payload: IWorkersState['selectedWorkerId']) {
    this.selectedWorkerId = payload;
  }

  @Mutation
  SET_SHOW_WORKERS(payload: { tab: keyof IWorkersState['showWorkers'], value: IWorkersState['showWorkers'][keyof IWorkersState['showWorkers']] }) {
    this.showWorkers[payload.tab] = payload.value;
  }

  @Mutation
  RESET_SHOW_WORKERS() {
    this.showWorkers = { ...DEFAULT_SHOW_WORKERS };
  }

  @Mutation
  SET_SELECTED_CLUSTER(payload: IWorkersState['selectedCluster']) {
    this.selectedCluster = payload;
  }

  @Mutation
  SET_SEARCH_WORKER_PROFESSION(payload: IWorkersState['searchWorkerProfession']) {
    this.searchWorkerProfession = payload;
  }

  @Mutation
  SET_WORKERS_GROUP(payload: IWorkersState['professionGroups']) {
    this.professionGroups = payload;
  }

  @Mutation
  SET_WORKERS_LIST(payload: IWorkersState['workersList']) {
    this.workersList = payload;
  }

  @Mutation
  SET_WORKERS_PAGINATION_PAGE(payload: number) {
    this.workersListPagination.page = payload;
  }

  @Mutation
  SET_WORKERS_PAGINATION_PER_PAGE(payload: number) {
    this.workersListPagination.perPage = payload;
  }

  @Mutation
  SET_WORKERS_PAGINATION_TOTAL(payload: number) {
    this.workersListPagination.total = payload;
  }

  @Mutation
  RESET_WORKERS_PAGINATION() {
    this.workersListPagination = { ...DEFAULT_PAGINATION };
  }

  @Mutation
  SET_WORKERS_FILTER(payload: IWorkersState['workersFilters']) {
    this.workersFilters = payload;
  }

  @Mutation
  RESET_WORKERS_FILTER_STATE() {
    this.filtersState = JSON.parse(JSON.stringify(DEFAULT_FILTERS_STATE));
  }

  @Action
  setSelectedCluster(payload: IWorkersState['selectedCluster']) {
    this.SET_SELECTED_FILTER_TYPE('cluster');
    this.SET_SELECTED_CLUSTER(payload);
  }

  @Action
  setSearchWorkerProfession(payload: IWorkersState['searchWorkerProfession']) {
    this.SET_SELECTED_FILTER_TYPE('profession');
    this.SET_SEARCH_WORKER_PROFESSION(payload);
  }

  @Action
  resetAllWorkersFilters() {
    this.SET_SELECTED_TAB(0);
    this.SET_WORKERS_LIST([]);
    this.RESET_WORKERS_FILTER_STATE();
    this.setSearchWorkerProfession(null);
    this.RESET_SHOW_WORKERS();
    this.SET_SELECTED_CLUSTER(null);
  }

  @Action
  async fetchWorkersProfessionsGroups(payload: any) {
    const data = await getWorkersGroups({
      ...payload,
      ...(this.selectedCluster && { cluster_id: this.selectedCluster.id }),
      ...(this.searchWorkerProfession && { profession_id: this.searchWorkerProfession.id }),
    });
    this.SET_WORKERS_GROUP(data.profession_groups);
  }

  @Action({ rawError: true })
  async fetchWorkersList(payload: any) {
    this.SET_WORKERS_LIST([]);
    const data = await getWorkersList({
      page: this.workersListPagination.page,
      limit: this.workersListPagination.perPage,
      ...payload,
      ...(this.searchWorkerProfession && { 'isco[]': this.searchWorkerProfession.id }),
      ...((this.selectedCluster && this.selectedWorkersTab === 0) && { filter_cluster: this.selectedCluster.id }),
      ...(this.filtersState.salarySize && { salary: this.filtersState.salarySize, salary_type: this.filtersState.selectedSalaryTypeId }),
      ...(this.filtersState.isImmediately && { immediately_available: 'on' }),
      ...(this.filterByLanguageQuality && { qual: this.filterByLanguageQuality }),
      ...(this.filtersState.selectedLand && { location: this.filtersState.selectedLand }),
      ...this.filterByLevelOfStudy,
      ...this.filterByTypeOfActivity,
      ...this.filterByRequirementLevel,
      ...((this.filtersState.orderDays && this.filtersState.orderMonth && this.filtersState.orderYear)
        && { availablity: `${this.filtersState.orderYear}-${this.filtersState.orderMonth}-${this.filtersState.orderDays}` }),
    });
    this.SET_WORKERS_LIST(data.applicants);
    this.SET_WORKERS_PAGINATION_TOTAL(data.params.total_rows);
  }

  @Action
  async fetchWorkersFiltersSettings(payload: any) {
    const data = await getWorkersFiltersSettings(payload);
    this.SET_WORKERS_FILTER(data);
  }
}

export const WorkersModule = getModule(Workers);
